import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { PlusIcon, PencilSquareIcon, KeyIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { appAdminApi } from '../services/api';
import { useUnitStore } from '../store/unitStore';
import { useAuthStore } from '../store/authStore';

type Tab = 'users' | 'devices' | 'audit';

const USER_ROLES = ['COMMANDER', 'OPERATOR', 'ANALYST', 'VIEWER', 'UNIT_COMMANDER', 'UNIT_OPERATOR', 'FIELD_OPERATOR'];
const USER_STATUSES = ['ACTIVE', 'INACTIVE', 'SUSPENDED'];
const DEVICE_STATUSES = ['ACTIVE', 'INACTIVE', 'LOST', 'COMPROMISED'];
const AUDIT_ACTIONS = [
  'LOGIN', 'LOGOUT', 'LOGIN_FAILED', 'USER_CREATED', 'USER_MODIFIED', 'USER_DELETED',
  'DEVICE_MODIFIED', 'DEVICE_DEACTIVATED', 'TARGET_CREATED', 'TARGET_MODIFIED', 'TARGET_ASSIGNED',
  'TARGET_ADJUDICATED', 'PHASE_TAGGED', 'OPERATION_CREATED', 'GEOFENCE_CREATED', 'GEOFENCE_MODIFIED',
];

interface AdminUser {
  id: string; email: string; firstName: string; lastName: string;
  role: string; status: string; unitId: string | null;
  lastLoginAt: string | null; unit?: { id: string; code: string; name: string } | null;
}
interface AdminDevice {
  id: string; deviceUUID: string; model: string | null; status: string;
  batteryLevel: number | null; signalStrength: number | null; lastSeenAt: string | null;
  platoon?: { id: string; callsign: string; unit?: { code: string } | null } | null;
}
interface AuditEntry {
  id: string; action: string; entityType: string; entityId: string; timestamp: string;
  user?: { email: string; firstName: string; lastName: string; role: string } | null;
}

function safeTime(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : format(d, 'HH:mm · dd MMM yy');
}

// ─── User editor / create modal ──────────────────────────────────────────────

function UserEditor({ user, onClose }: { user: AdminUser | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const units = useUnitStore((s) => s.units);
  const isEdit = !!user;

  const [email, setEmail] = useState(user?.email ?? '');
  const [firstName, setFirstName] = useState(user?.firstName ?? '');
  const [lastName, setLastName] = useState(user?.lastName ?? '');
  const [role, setRole] = useState(user?.role ?? 'VIEWER');
  const [status, setStatus] = useState(user?.status ?? 'ACTIVE');
  const [unitId, setUnitId] = useState(user?.unitId ?? '');
  const [password, setPassword] = useState('');

  const saveMutation = useMutation({
    mutationFn: () =>
      isEdit
        ? appAdminApi.updateUser(user!.id, { firstName, lastName, role, status, unitId: unitId || null })
        : appAdminApi.createUser({ email: email.trim(), firstName, lastName, role, unitId: unitId || undefined, password }),
    onSuccess: () => {
      toast.success(isEdit ? 'User updated' : 'User created');
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      onClose();
    },
    onError: (e: any) => toast.error(e?.response?.data?.error || 'Failed to save user'),
  });

  const canSave = isEdit ? firstName && lastName : email.trim() && firstName && lastName && password.length >= 6;

  return (
    <div className="fixed inset-0 z-[900] bg-black/70 flex items-start justify-center p-4 overflow-y-auto">
      <div className="w-full max-w-lg my-8 bg-command-panel border border-command-border rounded-xl">
        <div className="flex items-center justify-between p-4 border-b border-command-border">
          <h2 className="text-base font-semibold text-gray-100">{isEdit ? 'Edit User' : 'New User'}</h2>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-command-border/60" aria-label="Close">
            <XMarkIcon className="w-5 h-5 text-gray-400" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          {!isEdit && (
            <label className="block">
              <span className="block text-[11px] font-tactical text-gray-400 mb-1">EMAIL</span>
              <input value={email} onChange={(e) => setEmail(e.target.value)} type="email"
                className="w-full px-3 py-2 bg-command-bg border border-command-border rounded-lg text-gray-100 text-sm" />
            </label>
          )}
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-[11px] font-tactical text-gray-400 mb-1">FIRST NAME</span>
              <input value={firstName} onChange={(e) => setFirstName(e.target.value)}
                className="w-full px-3 py-2 bg-command-bg border border-command-border rounded-lg text-gray-100 text-sm" />
            </label>
            <label className="block">
              <span className="block text-[11px] font-tactical text-gray-400 mb-1">LAST NAME</span>
              <input value={lastName} onChange={(e) => setLastName(e.target.value)}
                className="w-full px-3 py-2 bg-command-bg border border-command-border rounded-lg text-gray-100 text-sm" />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-[11px] font-tactical text-gray-400 mb-1">ROLE</span>
              <select value={role} onChange={(e) => setRole(e.target.value)}
                className="w-full px-3 py-2 bg-command-bg border border-command-border rounded-lg text-gray-100 text-sm">
                {USER_ROLES.map((r) => <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>)}
              </select>
            </label>
            {isEdit && (
              <label className="block">
                <span className="block text-[11px] font-tactical text-gray-400 mb-1">STATUS</span>
                <select value={status} onChange={(e) => setStatus(e.target.value)}
                  className="w-full px-3 py-2 bg-command-bg border border-command-border rounded-lg text-gray-100 text-sm">
                  {USER_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
            )}
          </div>
          <label className="block">
            <span className="block text-[11px] font-tactical text-gray-400 mb-1">UNIT</span>
            <select value={unitId} onChange={(e) => setUnitId(e.target.value)}
              className="w-full px-3 py-2 bg-command-bg border border-command-border rounded-lg text-gray-100 text-sm">
              <option value="">No unit (Brigade HQ)</option>
              {units.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.code})</option>)}
            </select>
          </label>
          {!isEdit && (
            <label className="block">
              <span className="block text-[11px] font-tactical text-gray-400 mb-1">PASSWORD (min 6)</span>
              <input value={password} onChange={(e) => setPassword(e.target.value)} type="password"
                className="w-full px-3 py-2 bg-command-bg border border-command-border rounded-lg text-gray-100 text-sm" />
            </label>
          )}
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-command-border">
          <button type="button" onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-gray-100 border border-command-border">Cancel</button>
          <button type="button" onClick={() => saveMutation.mutate()} disabled={!canSave || saveMutation.isPending}
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-command-accent text-command-bg hover:bg-command-accent/90 disabled:opacity-50">
            {saveMutation.isPending ? 'Saving…' : isEdit ? 'Save User' : 'Create User'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Users tab ───────────────────────────────────────────────────────────────

function UsersTab() {
  const [editing, setEditing] = useState<{ open: boolean; user: AdminUser | null }>({ open: false, user: null });

  const usersQ = useQuery({
    queryKey: ['admin-users'],
    queryFn: async () => (await appAdminApi.getUsers()).data?.data?.users as AdminUser[],
    retry: false,
  });

  const resetMutation = useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) => appAdminApi.resetPassword(id, password),
    onSuccess: () => toast.success('Password reset — user must re-login'),
    onError: (e: any) => toast.error(e?.response?.data?.error || 'Failed to reset password'),
  });

  const doReset = (u: AdminUser) => {
    const pw = window.prompt(`New password for ${u.email} (min 6 chars):`);
    if (pw && pw.length >= 6) resetMutation.mutate({ id: u.id, password: pw });
    else if (pw !== null) toast.error('Password must be at least 6 characters');
  };

  const users = usersQ.data ?? [];

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <button type="button" onClick={() => setEditing({ open: true, user: null })}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-command-accent text-command-bg text-sm font-semibold hover:bg-command-accent/90">
          <PlusIcon className="w-4 h-4" /> New User
        </button>
      </div>
      <div className="rounded-xl border border-command-border bg-command-panel overflow-hidden">
        <div className="overflow-x-auto [-webkit-overflow-scrolling:touch]">
          <table className="w-full min-w-[720px] text-sm">
            <thead>
              <tr className="border-b border-command-border text-gray-400 text-[11px] uppercase tracking-wide">
                <th className="text-left py-2 px-3 font-semibold">Email</th>
                <th className="text-left py-2 px-3 font-semibold">Name</th>
                <th className="text-left py-2 px-3 font-semibold">Role</th>
                <th className="text-left py-2 px-3 font-semibold">Status</th>
                <th className="text-left py-2 px-3 font-semibold">Unit</th>
                <th className="text-left py-2 px-3 font-semibold">Last Login</th>
                <th className="text-right py-2 px-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-command-border/60">
                  <td className="py-2 px-3 text-gray-200">{u.email}</td>
                  <td className="py-2 px-3 text-gray-300">{u.firstName} {u.lastName}</td>
                  <td className="py-2 px-3 text-gray-400 text-xs">{u.role.replace(/_/g, ' ')}</td>
                  <td className="py-2 px-3">
                    <span className={clsx('px-2 py-0.5 rounded-full text-[10px] font-bold',
                      u.status === 'ACTIVE' ? 'bg-command-success/20 text-command-success' :
                      u.status === 'SUSPENDED' ? 'bg-severity-critical/20 text-severity-critical' : 'bg-command-border text-gray-400')}>
                      {u.status}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-gray-400">{u.unit?.code ?? 'HQ'}</td>
                  <td className="py-2 px-3 text-gray-500 text-xs">{safeTime(u.lastLoginAt)}</td>
                  <td className="py-2 px-3">
                    <div className="flex items-center justify-end gap-1">
                      <button type="button" onClick={() => setEditing({ open: true, user: u })}
                        className="p-1.5 rounded hover:bg-command-border/60" aria-label="Edit user">
                        <PencilSquareIcon className="w-4 h-4 text-gray-400" />
                      </button>
                      <button type="button" onClick={() => doReset(u)}
                        className="p-1.5 rounded hover:bg-command-border/60" aria-label="Reset password">
                        <KeyIcon className="w-4 h-4 text-severity-medium" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr><td colSpan={7} className="py-8 text-center text-gray-500 text-sm">
                  {usersQ.isError ? 'Admin API unavailable.' : 'No users.'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {editing.open && <UserEditor user={editing.user} onClose={() => setEditing({ open: false, user: null })} />}
    </div>
  );
}

// ─── Devices tab ─────────────────────────────────────────────────────────────

function DevicesTab() {
  const queryClient = useQueryClient();
  const devicesQ = useQuery({
    queryKey: ['admin-devices'],
    queryFn: async () => (await appAdminApi.getDevices()).data?.data?.devices as AdminDevice[],
    retry: false,
    refetchInterval: 15000,
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) => appAdminApi.setDeviceStatus(id, status),
    onSuccess: () => { toast.success('Device status updated'); queryClient.invalidateQueries({ queryKey: ['admin-devices'] }); },
    onError: (e: any) => toast.error(e?.response?.data?.error || 'Failed to update device'),
  });

  const devices = devicesQ.data ?? [];

  return (
    <div className="rounded-xl border border-command-border bg-command-panel overflow-hidden">
      <div className="overflow-x-auto [-webkit-overflow-scrolling:touch]">
        <table className="w-full min-w-[720px] text-sm">
          <thead>
            <tr className="border-b border-command-border text-gray-400 text-[11px] uppercase tracking-wide">
              <th className="text-left py-2 px-3 font-semibold">Platoon</th>
              <th className="text-left py-2 px-3 font-semibold">Unit</th>
              <th className="text-left py-2 px-3 font-semibold">Model</th>
              <th className="text-center py-2 px-3 font-semibold">Battery</th>
              <th className="text-center py-2 px-3 font-semibold">Signal</th>
              <th className="text-left py-2 px-3 font-semibold">Last Seen</th>
              <th className="text-left py-2 px-3 font-semibold">Status</th>
            </tr>
          </thead>
          <tbody>
            {devices.map((d) => (
              <tr key={d.id} className="border-b border-command-border/60">
                <td className="py-2 px-3 text-gray-200">{d.platoon?.callsign ?? '—'}</td>
                <td className="py-2 px-3 text-gray-400">{d.platoon?.unit?.code ?? '—'}</td>
                <td className="py-2 px-3 text-gray-400 text-xs">{d.model ?? '—'}</td>
                <td className={clsx('py-2 px-3 text-center font-tactical',
                  (d.batteryLevel ?? 100) < 20 ? 'text-severity-critical' : 'text-gray-300')}>
                  {d.batteryLevel != null ? `${d.batteryLevel}%` : '—'}
                </td>
                <td className="py-2 px-3 text-center text-gray-300 font-tactical">{d.signalStrength ?? '—'}</td>
                <td className="py-2 px-3 text-gray-500 text-xs">{safeTime(d.lastSeenAt)}</td>
                <td className="py-2 px-3">
                  <select aria-label={`Status for ${d.platoon?.callsign ?? 'device'}`} value={d.status}
                    onChange={(e) => statusMutation.mutate({ id: d.id, status: e.target.value })}
                    className="px-2 py-1 rounded bg-command-bg border border-command-border text-xs text-gray-100">
                    {DEVICE_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
              </tr>
            ))}
            {devices.length === 0 && (
              <tr><td colSpan={7} className="py-8 text-center text-gray-500 text-sm">
                {devicesQ.isError ? 'Admin API unavailable.' : 'No devices registered.'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Audit tab ───────────────────────────────────────────────────────────────

function AuditTab() {
  const [action, setAction] = useState('');
  const [since, setSince] = useState('');
  const [page, setPage] = useState(1);

  const auditQ = useQuery({
    queryKey: ['admin-audit', action, since, page],
    queryFn: async () =>
      (await appAdminApi.getAudit({
        action: action || undefined,
        since: since ? new Date(since).toISOString() : undefined,
        page,
        limit: 50,
      })).data?.data as { entries: AuditEntry[]; pagination: { total: number; totalPages: number } },
    retry: false,
    refetchInterval: 20000,
  });

  const entries = auditQ.data?.entries ?? [];
  const totalPages = auditQ.data?.pagination?.totalPages ?? 1;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <select aria-label="Filter by action" value={action} onChange={(e) => { setAction(e.target.value); setPage(1); }}
          className="px-2 py-1.5 rounded bg-command-panel border border-command-border text-xs text-gray-100">
          <option value="">All actions</option>
          {AUDIT_ACTIONS.map((a) => <option key={a} value={a}>{a.replace(/_/g, ' ')}</option>)}
        </select>
        <input type="datetime-local" aria-label="Since" value={since} onChange={(e) => { setSince(e.target.value); setPage(1); }}
          className="px-2 py-1.5 rounded bg-command-panel border border-command-border text-xs text-gray-100" />
      </div>
      <div className="rounded-xl border border-command-border bg-command-panel overflow-hidden">
        <div className="overflow-x-auto [-webkit-overflow-scrolling:touch]">
          <table className="w-full min-w-[640px] text-sm">
            <thead>
              <tr className="border-b border-command-border text-gray-400 text-[11px] uppercase tracking-wide">
                <th className="text-left py-2 px-3 font-semibold">Time</th>
                <th className="text-left py-2 px-3 font-semibold">Action</th>
                <th className="text-left py-2 px-3 font-semibold">User</th>
                <th className="text-left py-2 px-3 font-semibold">Entity</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id} className="border-b border-command-border/60">
                  <td className="py-2 px-3 text-gray-500 text-xs font-tactical whitespace-nowrap">{safeTime(e.timestamp)}</td>
                  <td className="py-2 px-3 text-gray-200 text-xs">{e.action.replace(/_/g, ' ')}</td>
                  <td className="py-2 px-3 text-gray-400 text-xs">{e.user ? `${e.user.firstName} ${e.user.lastName}` : 'system'}</td>
                  <td className="py-2 px-3 text-gray-500 text-xs">{e.entityType} {e.entityId?.slice(0, 8)}</td>
                </tr>
              ))}
              {entries.length === 0 && (
                <tr><td colSpan={4} className="py-8 text-center text-gray-500 text-sm">
                  {auditQ.isError ? 'Admin API unavailable.' : 'No audit entries.'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 text-sm">
          <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
            className="px-3 py-1 rounded border border-command-border text-gray-300 disabled:opacity-40">Prev</button>
          <span className="text-gray-500 text-xs">Page {page} / {totalPages}</span>
          <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1 rounded border border-command-border text-gray-300 disabled:opacity-40">Next</button>
        </div>
      )}
    </div>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────

export default function AdminPage() {
  const { user } = useAuthStore();
  const { isHQ, units, fetchUnits } = useUnitStore();
  const userIsHQ = isHQ(user?.unitId ?? null);
  const [tab, setTab] = useState<Tab>('users');

  useEffect(() => {
    if (userIsHQ && units.length === 0) fetchUnits();
  }, [userIsHQ, units.length, fetchUnits]);

  if (!userIsHQ) {
    return (
      <div className="p-6">
        <h1 className="text-lg font-display font-bold text-gray-100">Admin</h1>
        <p className="text-gray-500 mt-2 text-sm">Superuser (Brigade HQ) only.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-lg font-display font-bold text-gray-100">Admin Console</h1>
        <p className="text-gray-500 text-[11px]">Users · device fleet · audit trail</p>
      </div>

      <div className="flex gap-2">
        {([
          { id: 'users', label: 'Users' },
          { id: 'devices', label: 'Devices' },
          { id: 'audit', label: 'Audit' },
        ] as { id: Tab; label: string }[]).map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)}
            className={clsx('px-4 py-2 rounded-lg text-sm font-medium transition-colors touch-manipulation',
              tab === t.id ? 'bg-command-accent text-command-bg'
                : 'bg-command-panel border border-command-border text-gray-400 hover:text-white hover:border-gray-500')}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'users' ? <UsersTab /> : tab === 'devices' ? <DevicesTab /> : <AuditTab />}
    </div>
  );
}
